import React, { useState, useRef } from "react";
import { Card } from "../../Card";
import moment from "moment";
import { formatMoney } from "../../../configs/formatCurrency";
import toastMessage from "../../../components/toastMessage";
import HOST_URL from "../../../configs/api";
import Axios from "axios";
import { saveAs } from "file-saver";
import { Modal } from "../../Modal";
import StatusBadge from "../../StatusBadge";

const Loan = ({
  loans,
  sending_sms,
  response,
  onPressFileImport,
  fromDate,
  toDate,
  app_id,
  ztoken,
  user_id,
  pageLimit,
  currentPage,
  status,
  member,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isImporting, setIsImporting] = useState(false);
  const fileInputRef = useRef(null);

  const downloadFile = (file, type) => {
    try {
      toastMessage("success", "Downloaded successfully");
      setIsSubmitting(false);
      const fileBlob = new Blob([file], { type: `application/${type}` });
      saveAs(
        fileBlob,
        `loans_${fromDate}_${toDate}.${type}`
      );
    } catch (error) {
      setIsSubmitting(false);
      toastMessage("Error downloading file:", JSON.stringify(error));
    }
  }

  const handleGenerateReport = (type='csv') => {
    setIsSubmitting(true);
    const options = {
      method: "GET",
      url: `${HOST_URL.url}/loans-v2/${type}`,
      responseType: "blob",
      params: {
        from_date: fromDate,
        to_date: toDate,
        app_id: app_id,
        status: status?.value ? status?.value : ["approved","disbursed","cleared", "paused"].join(","),
        page_limit: pageLimit,
        member_id: member?._id ? member?._id : undefined,
      },
      headers: {
        authorization: `Bearer ${ztoken}`,
        //"Content-Type": `application/${type}`,
      },
    };

    Axios(options)
      .then((data) => {
        downloadFile(data.data, type);
      })
      .catch((error) => {
        console.log(error?.response);
        toastMessage("error", error?.response?.data?.error || "Unable to generate loans statement report");
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  }

  const onDownloadCsvTemplate = () => {
    setIsSubmitting(true);
    const options = {
      method: "GET",
      url: `${HOST_URL.url}/loans-v2/csv-template`,
      responseType: "blob",
      headers: {
        authorization: `Bearer ${ztoken}`,
        "Content-Type": `application/csv`,
      },
    };
    Axios(options)
      .then((data) => {
        downloadFile(data.data, "csv");
      })
      .catch((error) => {
        setIsSubmitting(false);
        toastMessage("error", error?.response?.data?.error || "Unable to generate loans statement report");
      });
  }

  const handleFileUpload = async (e) => {
    e.preventDefault();
    if (!selectedFile) {
      toastMessage("error", "Please select a file to upload");
      return;
    }

    const formData = new FormData();
    formData.append("loans_csv_file", selectedFile);
    formData.append("app_id", app_id);

    try {
      setUploadProgress(0);
      setIsImporting(true);
      const response = await Axios({
        method: "POST",
        url: `${HOST_URL.url}/loans-v2/import`,
        data: formData,
        headers: {
          "Content-Type": "multipart/form-data",
          authorization: `Bearer ${ztoken}`,
        },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(progress);
        },
      });

      toastMessage("success", response?.data?.message || "Loans imported successfully");
      setShowImportModal(false);
      setSelectedFile(null);
      setUploadProgress(0);
      if (fileInputRef.current) fileInputRef.current.value = "";
    } catch (error) {
      toastMessage("error", error?.response?.data?.error || "Failed to import loans");
      if (fileInputRef.current) fileInputRef.current.value = "";
    } finally {
      setIsImporting(false);
      setUploadProgress(0);
      setSelectedFile(null);
    }
  };

  return (
    <Card className="col-md-12">
      {sending_sms ? (
        <div className="alert alert-info">
          <span>
            <i className="fa fa-spin fa-spinner"></i>{" "}
            {response.sms_sending_message}
          </span>
        </div>
      ) : (
        response.success && (
          <div className="alert alert-success">
            <span>{response.success}</span>
          </div>
        )
      )}
      <div className="panel-heading card-title">
        <span>Loans</span>
        <div className="card-menus font-size-12">
        <div
            onClick={() => onDownloadCsvTemplate()}
            className="icon-default"
            style={{ marginRight: 10, fontSize: 14 }}
          >
            <i className="fa fa-file-excel-o"></i> Loans Template
          </div>
          <div
            onClick={() => setShowImportModal(true)}
            className="icon-default"
            style={{ marginRight: 10, fontSize: 14 }}
          >
            <i className="fa fa-cloud-upload"></i> Import Loans
          </div>

          <div className="icon-default" style={{ fontSize: 14 }}>
            <div
              onClick={() => handleGenerateReport('csv')}
            >
              <i className="fa fa-cloud-download"></i>
              {isSubmitting ? " Downloading Report..." : " Download CSV Report"}
            </div>
          </div>
        </div>
      </div>
      <div className="panel-body table-responsive">
        <table className="table table-bordered table-striped">
          <thead>
            <tr>
              <th>#</th>
              <th>Loan ID</th>
              <th>Member Name</th>
              <th>Product</th>
              <th className="text-right">Principal</th>
              <th className="text-right">Interest</th>
              <th className="text-right">Penalties</th>
              <th className="text-right">Amt. Paid</th>
              <th className="text-right">Balance</th>
              <th>Issued At</th>
              <th className="text-center">Loan Status</th>
              {/* <th className="text-center">Payment Status</th> */}
              {/* <th></th>
              <th>Actions</th> */}
            </tr>
          </thead>

          <tbody>
            {loans.length > 0 ? (
              loans.map((item, i) => {
                let isDefaulted = item.payment_status === "Behind Schedule";
                const counter = (currentPage - 1) * pageLimit + (i + 1);
                
                return (
                  <tr
                    key={i}
                    style={
                      isDefaulted
                        ? { background: "#fae3e3" }
                        : { background: "#fffff" }
                    }
                  >
                    <td>{counter}</td>
                    <td>
                      <a href={`view_loan_payments/${item.loan_id}`} className="text-primary">
                        {item.loan_id}
                      </a>
                    </td>
                    <td>{item.member?.user_name?.toUpperCase()}</td>
                    <td>{item.loan_product?.name?.toUpperCase()} {`(${item?.duration} ${item?.duration_unit})`}</td>
                    <td className="text-right">KES {formatMoney(item?.principal_amount)}</td>
                    <td className="text-right">KES {formatMoney(item?.total_interest)}</td>
                    <td className="text-right">KES {formatMoney(item?.total_penalties)}</td>
                    <td className="text-right">KES {formatMoney(item?.total_repayments)}</td>
                    <td className="text-right">KES {formatMoney(item?.balance)}</td>
                    <td>{moment(item.start_date).format("ll")}</td>
                    <td className="text-center">
                      <StatusBadge status={item.status} type="loan" />
                    </td>
                    {/* <td className="text-center">
                      <span
                        className={`badge badge-${
                          item.payment_status === "On Schedule"
                            ? "info"
                            : item.payment_status === "Behind Schedule"
                            ? "danger"
                            : "cleared"
                        }`}
                      >
                        {item.payment_status?.toUpperCase()}
                      </span>
                    </td> */}
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={11} align="center">
                  No data here
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <Modal show={showImportModal}>
        <div className="modal-header">
          <h5 className="modal-title">Import Loans from CSV</h5>
          <button
            type="button"
            className="close"
            onClick={() => setShowImportModal(false)}
          >
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
        <div className="modal-body">
          <div className="alert alert-info">
            <i className="fa fa-info-circle"></i> Please ensure your CSV file matches the template.
            <div className="mt-2">
              <small>
                <span
                  className="text-primary"
                  style={{ cursor: "pointer" }}
                  onClick={() => {
                    onDownloadCsvTemplate();
                    setShowImportModal(false);
                  }}
                >
                  Download Template
                </span>
              </small>
            </div>
          </div>

          <div className="form-group">
            <label>Select CSV File</label>
            <input
              type="file"
              className="form-control"
              accept=".csv"
              onChange={(e) => setSelectedFile(e.target.files[0])}
              ref={fileInputRef}
            />
          </div>

          {uploadProgress > 0 && (
            <div className="progress mt-3">
              <div
                className="progress-bar progress-bar-striped progress-bar-animated"
                role="progressbar"
                style={{ width: `${uploadProgress}%` }}
                aria-valuenow={uploadProgress}
                aria-valuemin="0"
                aria-valuemax="100"
              >
                {uploadProgress}%
              </div>
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => setShowImportModal(false)}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleFileUpload}
            disabled={!selectedFile}
          >
            {isImporting ? "Uploading..." : "Import"}
          </button>
        </div>
      </Modal>
    </Card>
  );
};
export default Loan;
